import { onBeforeUnmount, onMounted, ref } from 'vue';

import { BREAKPOINTS } from '#/composables/useBreakpoint';

/**
 * hover 이탈 후 실제로 닫히기까지의 유예(ms).
 *
 * 전폭 메가메뉴는 버튼줄과 패널이 **서로 다른 DOM 서브트리**라, 버튼에서 패널로 마우스를
 * 옮기는 도중 경계를 스치면 `mouseleave` 가 발화해 패널이 사라진다. 유예를 두면 그 미세
 * 이탈을 흡수하고, 유예 안에 패널로 진입하면 `hoverKeep()` 이 예약을 취소한다.
 */
const CLOSE_DELAY_MS = 150;

/**
 * 데스크톱 GNB 드롭다운 열림 상태 (#118 — 멀티 메뉴 전환).
 *
 * 프로토타입 `guide/guide-nav.js` 의 DOM 클래스 토글(`.gnb-item.open`)을 Vue 상태로 이관했다.
 * 열린 대분류를 **라벨 하나**로만 들고 있어(동시 1개), 패널 간 전환이 자연스럽다.
 * 전폭 메가메뉴로 바뀐 뒤에도 이 라벨은 유효하다 — 패널은 한 장이지만 **어느 대분류가
 * 열렸는지**가 컬럼 헤더 강조와 버튼 밑줄을 가른다.
 *
 * 동작 규약:
 *  - hover(lg↑): 진입 시 열고, 이탈 시 CLOSE_DELAY_MS 유예 후 닫는다. 터치 기기는 hover 가
 *    없어 클릭만 동작.
 *  - 클릭: 같은 대분류면 토글(닫기), 다른 대분류면 그쪽으로 교체.
 *  - 외부 클릭·ESC: 즉시 닫는다.
 *
 * ⚠️ 리스너는 컴포넌트 생명주기에 묶는다 — `guide-nav.js` 는 전역에 영구 등록했지만,
 * SPA 에서는 헤더가 재마운트될 때마다 리스너가 누적되므로 onBeforeUnmount 에서 해제한다.
 * SSG(vite-ssg) 프리렌더에는 document 가 없어 onMounted 안에서만 등록한다.
 */
export function useGnbMenu() {
  /** 현재 열린 대분류 라벨(null=닫힘). 동시에 하나만 열린다. */
  const openLabel = ref<string | null>(null);

  /**
   * 클릭으로 "고정"된 대분류.
   *
   * ⚠️ hover 와 클릭이 `openLabel` 하나만 공유하면 **데스크톱에서 클릭이 영영 안 먹는다** —
   * 마우스가 버튼에 닿는 순간 hover 가 이미 열어 두므로, 이어지는 클릭의 토글이 항상
   * "열림 → 닫힘"으로 해석되기 때문이다. 클릭 의도를 별도 상태로 분리해,
   * 고정된 동안에는 hover 이탈이 닫지 못하게 한다(터치 기기·접근성 키보드 조작 대비).
   */
  const pinnedLabel = ref<string | null>(null);

  /**
   * 예약된 지연 닫기 타이머.
   *
   * 반응형일 이유가 없어 `ref` 가 아닌 plain 변수로 둔다 — 렌더 트리거를 만들지 않는다.
   * 생성은 `hoverClose()` 에서만 일어나고, 그 함수가 `typeof window` 가드로 시작하므로
   * SSG 프리렌더 중에는 절대 만들어지지 않는다.
   */
  let closeTimer: ReturnType<typeof setTimeout> | null = null;

  /** 예약된 닫기를 취소 — 유예 안에 hover 가 돌아온 경우. */
  function cancelClose(): void {
    if (closeTimer !== null) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
  }

  function close(): void {
    cancelClose();
    openLabel.value = null;
    pinnedLabel.value = null;
  }

  /**
   * 클릭 토글 — 고정 상태를 기준으로 판단한다.
   * hover 로 열려 있을 뿐 고정되지 않았다면, 클릭은 "닫기"가 아니라 "고정"이다.
   */
  function toggle(label: string): void {
    cancelClose();
    if (pinnedLabel.value === label) {
      close();
      return;
    }
    pinnedLabel.value = label;
    openLabel.value = label;
  }

  /**
   * hover 진입 — 데스크톱(lg↑)에서만 연다.
   * 모바일·터치 폭에서는 아코디언이 담당하므로 hover 로 패널이 튀어나오지 않게 막는다.
   *
   * 예약된 닫기를 먼저 취소한다 — 대분류 A→B 로 옮겨갈 때 A 의 이탈 타이머가 살아 있으면
   * B 를 연 직후 그 타이머가 패널을 닫아버린다.
   */
  function hoverOpen(label: string): void {
    if (typeof window === 'undefined') return;
    if (window.innerWidth < BREAKPOINTS.lg) return;
    cancelClose();
    openLabel.value = label;
  }

  /**
   * hover 이탈 — 즉시 닫지 않고 CLOSE_DELAY_MS 유예 후 닫는다(버튼↔패널 왕복 흡수).
   * 클릭으로 고정된 패널은 유지한다.
   *
   * `close()` 가 아니라 `openLabel` 만 비우는 이유 — 고정 상태(pinned)는 위에서 early-return
   * 으로 걸러지므로 여기 도달한 시점엔 pinned 가 이미 null 이다.
   */
  function hoverClose(): void {
    if (typeof window === 'undefined') return;
    if (pinnedLabel.value !== null) return;
    if (window.innerWidth < BREAKPOINTS.lg) return;
    cancelClose();
    closeTimer = setTimeout(() => {
      closeTimer = null;
      openLabel.value = null;
    }, CLOSE_DELAY_MS);
  }

  /**
   * 패널 위로 hover 복귀 — 예약된 닫기만 취소한다(열린 라벨은 그대로).
   * 전폭 패널이 버튼줄과 별개 영역이라, 패널에 진입했다는 신호가 따로 필요하다.
   */
  function hoverKeep(): void {
    cancelClose();
  }

  /** 열림 여부 조회(템플릿 가독성용). */
  function isOpen(label: string): boolean {
    return openLabel.value === label;
  }

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') close();
  }

  onMounted(() => {
    // 헤더 밖 클릭으로 닫기 — 헤더 내부 클릭은 AppHeader 가 @click.stop 으로 차단한다.
    document.addEventListener('click', close);
    window.addEventListener('keydown', onKeydown);
  });

  onBeforeUnmount(() => {
    // 타이머 먼저 정리 — 언마운트 후 콜백이 깨어나 ref 를 건드리는 것을 막는다.
    cancelClose();
    document.removeEventListener('click', close);
    window.removeEventListener('keydown', onKeydown);
  });

  return { openLabel, isOpen, toggle, hoverOpen, hoverClose, hoverKeep, close };
}
